import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { notificationAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

// Get WebSocket URL from backend URL
const getWebSocketUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s) to ws(s)
  return backendUrl.replace(/^http/, 'ws');
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  // V4.17.2 — Track which user this provider is currently bound to so
  // we can hard-cycle the WebSocket on login/logout/role-switch instead
  // of leaving an admin's WS open while an agent is now logged in.
  const boundUserIdRef = useRef(null);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getNotifications({ limit: 50 });
      setNotifications(res.data || []);
      setUnreadCount(res.data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    // Use /api/ws prefix for Kubernetes ingress compatibility
    const wsUrl = `${getWebSocketUrl()}/api/ws/notifications/${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'notification') {
            // Add new notification to state
            const notification = data.notification;
            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            toast.info(notification.title, {
              description: notification.message,
              duration: 5000,
              action: notification.data?.reference_id ? {
                label: 'View',
                onClick: () => {
                  const type = notification.data?.reference_type;
                  const id = notification.data?.reference_id;
                  if (type === 'booking' || type === 'payment') {
                    window.location.href = id ? `/bookings/${id}` : '/bookings';
                  } else if (type === 'commission_claim') {
                    window.location.href = id ? `/claims/${id}` : '/claims';
                  } else if (type === 'appointment') {
                    window.location.href = '/appointments';
                  } else if (type === 'kyc') {
                    window.location.href = '/admin/kyc';
                  } else if (type === 'property') {
                    window.location.href = id ? `/properties/${id}` : '/admin/properties';
                  } else if (type === 'agent') {
                    window.location.href = id ? `/admin/agents/${id}` : '/admin/agents';
                  }
                }
              } : undefined
            });
          } else if (data.type === 'unread_count') {
            setUnreadCount(data.count);
          } else if (data.type === 'marked_read') {
            setNotifications(prev => 
              prev.map(n => 
                n.id === data.notification_id ? { ...n, is_read: true } : n
              )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Reconnect after delay (if not intentionally closed)
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect WebSocket...');
            connect();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, []);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User logout');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    // Send via WebSocket if connected
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ 
        type: 'mark_read', 
        notification_id: notificationId 
      }));
    } else {
      // Fallback to REST API
      try {
        await notificationAPI.markRead(notificationId);
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Request unread count via WebSocket
  const refreshUnreadCount = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'get_unread_count' }));
    }
  }, []);

  // V4.17.2 — Single source of truth: react to `user` identity changes.
  // The old code (mount-only effect + 2-second localStorage polling +
  // cross-tab storage listener) raced on same-tab logout/login, leaving
  // a stale WebSocket bound to the previous user's token. Symptom: white
  // screen after admin logout → agent login on the same browser session.
  //
  // Rules:
  //   - User identity changed (login, logout, or switch) → tear down the
  //     existing WS + clear notification state, then reconnect with the
  //     new token if a user is now present.
  //   - Same user → no-op (deps stable).
  useEffect(() => {
    const currentId = user?.id || null;
    if (boundUserIdRef.current === currentId) {
      return;
    }
    // Identity changed (or initial mount with a stored token). Tear
    // down whatever was bound before.
    disconnect();
    setNotifications([]);
    setUnreadCount(0);
    boundUserIdRef.current = currentId;
    if (currentId) {
      fetchNotifications();
      connect();
    }
    return () => {
      // Cleanup on unmount only — identity-change cleanup runs above.
    };
  }, [user?.id, connect, disconnect, fetchNotifications]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      isConnected,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
      fetchNotifications,
      connect,
      disconnect
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
